import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { Company, CompanyMember } from "../api/models";
import { getMyCompany, getCompanyMembers } from "../api/api";
import { getCurrentUser } from "../api/currentUser";
import { useMsalAuth, getToken } from "../authConfig";
import { useMsal } from "@azure/msal-react";
import { useOrganization } from "./OrganizationContext";

interface CompanyContextType {
    company: Company | null;
    companyRole: "admin" | "member";
    roleReady: boolean;
    members: CompanyMember[];
    loading: boolean;
    error: string | null;
    refreshCompany: () => Promise<void>;
    refreshMembers: () => Promise<void>;
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

export const CompanyProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [company, setCompany] = useState<Company | null>(null);
    const [members, setMembers] = useState<CompanyMember[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [userHasCompany, setUserHasCompany] = useState<boolean | null>(null);
    const [companyRole, setCompanyRole] = useState<"admin" | "member">("member");
    const [roleReady, setRoleReady] = useState(false);

    const client = useMsalAuth ? useMsal().instance : undefined;
    const { context: organizationContext, isOrganizationAdmin } = useOrganization();

    const refreshCompany = async () => {
        try {
            setLoading(true);
            setError(null);

            const token = client ? await getToken(client) : undefined;
            console.log("[CompanyContext] Calling getMyCompany...");
            const companyData = await getMyCompany(token);
            console.log("[CompanyContext] getMyCompany returned:", companyData);

            setCompany(companyData);
            setUserHasCompany(companyData !== null);
        } catch (err) {
            console.error("[CompanyContext] Failed to load company:", err);
            setError(err instanceof Error ? err.message : "Failed to load company");
            setUserHasCompany(false);
        } finally {
            setLoading(false);
        }
    };

    const refreshMembers = async () => {
        // Only fetch members if user has a company
        if (userHasCompany === false) {
            return;
        }

        try {
            setLoading(true);
            setError(null);

            const token = client ? await getToken(client) : undefined;
            const membersData = await getCompanyMembers(token);

            setMembers(membersData);
        } catch (err) {
            console.error("Failed to load company members:", err);
            setError(err instanceof Error ? err.message : "Failed to load company members");
        } finally {
            setLoading(false);
        }
    };

    // Check if user has company by fetching /auth/me
    useEffect(() => {
        const checkUserCompany = async () => {
            try {
                const token = client ? await getToken(client) : undefined;
                const response = await getCurrentUser({ idToken: token });

                if (response.ok) {
                    const userData = response.user;
                    console.log("[CompanyContext] /auth/me response:", userData);
                    console.log("[CompanyContext] company:", userData?.company);
                    console.log("[CompanyContext] company_id:", userData?.company_id);

                    // Check both company object (when company_service works) and company_id (fallback)
                    const hasCompany =
                        (userData?.company?.id !== null && userData?.company?.id !== undefined) ||
                        (userData?.company_id !== null && userData?.company_id !== undefined) ||
                        Boolean(organizationContext?.currentOrganizationId);
                    console.log("[CompanyContext] hasCompany:", hasCompany);
                    setCompanyRole(userData?.company_role === "admin" || isOrganizationAdmin ? "admin" : "member");
                    setRoleReady(true);
                    setUserHasCompany(hasCompany);

                    // If user has company, load company and members
                    if (hasCompany) {
                        console.log("[CompanyContext] Loading company data...");
                        await refreshCompany();
                        await refreshMembers();
                    }
                } else {
                    setRoleReady(true);
                }
            } catch (err) {
                console.error("Failed to check user company:", err);
                setUserHasCompany(false);
                setRoleReady(true);
            }
        };

        if (client || !useMsalAuth) {
            checkUserCompany();
        }
    }, [client, isOrganizationAdmin, organizationContext?.currentOrganizationId]);

    return (
        <CompanyContext.Provider
            value={{
                company,
                companyRole,
                roleReady,
                members,
                loading,
                error,
                refreshCompany,
                refreshMembers
            }}
        >
            {children}
        </CompanyContext.Provider>
    );
};

export const useCompany = () => {
    const context = useContext(CompanyContext);
    if (context === undefined) {
        throw new Error("useCompany must be used within a CompanyProvider");
    }
    return context;
};
